/**
 * CDN origins, and how local development reaches them.
 *
 * Images are served from authenticated CDN origins: a `POST {cdn}/auth/` exchanges the
 * session token for a cookie, and without that cookie every image returns
 * `403 {"status":"unauthorized"}`, which the browser discards as a non-image response.
 * The result is silently broken images.
 *
 * That auth call cannot succeed from `http://localhost`. It sends
 * `Content-Type: application/json`, so the browser preflights it, and the CDN answers
 * the preflight only for deployed Control origins — from localhost it replies `200`
 * with no `Access-Control-Allow-*` headers and the browser blocks the request. The dev
 * server therefore proxies each configured CDN under `/cdn/<hostname>` (see
 * `src/setupProxy.js`), which makes the auth call and the image requests same-origin
 * and removes CORS from the picture entirely. Deployed builds are unaffected: the
 * rewrite below is inert outside local development.
 */
const LOCAL_CDN_PROXY_PATH = '/cdn';

/**
 * Every CDN origin the app authenticates against, in configuration order.
 *
 * `REACT_APP_CDN_URLS` is a JSON map of region key to origin; `REACT_APP_CDN_URL` is
 * the single-origin form.
 */
export const getCdnUrls = (): string[] => {
  if (process.env.REACT_APP_CDN_URLS) {
    const cdnUrls = JSON.parse(process.env.REACT_APP_CDN_URLS);
    return Object.values(cdnUrls);
  }
  if (process.env.REACT_APP_CDN_URL) {
    return [process.env.REACT_APP_CDN_URL];
  }
  throw new Error('No CDN URLs configured');
};

const isLocalDevelopmentOrigin = (): boolean => {
  if (process.env.NODE_ENV !== 'development') {
    return false;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

/**
 * The dev-server mount point for one CDN origin.
 *
 * Keyed by hostname rather than by position so that the ordering of the configured
 * list cannot desynchronise the two sides, and so the proxy only ever forwards to
 * hosts that are explicitly configured.
 *
 * `setupProxy.js` is CommonJS loaded by the dev server and cannot import this module,
 * so it builds the same `/cdn/<hostname>` path itself. The two constructions must be
 * changed together; there is no test or type that will catch a drift.
 */
const cdnProxyMountPath = (cdnUrl: string): string | null => {
  try {
    return `${LOCAL_CDN_PROXY_PATH}/${new URL(cdnUrl).hostname}`;
  } catch {
    return null;
  }
};

const getProxiedCdnOrigins = (): string[] => {
  try {
    return getCdnUrls();
  } catch {
    return [];
  }
};

/**
 * Rewrites an absolute CDN URL onto the local dev proxy; a no-op everywhere else.
 *
 * The rewrite is a byte-exact prefix swap rather than a `URL` round trip, because the
 * CDN's cache key is the literal query string and re-encoding it would miss the cache.
 */
export const toCdnRequestUrl = (url: string): string => {
  if (!url || !isLocalDevelopmentOrigin()) {
    return url;
  }

  for (const cdnUrl of getProxiedCdnOrigins()) {
    let origin: string;
    try {
      origin = new URL(cdnUrl).origin;
    } catch {
      continue;
    }
    const mountPath = cdnProxyMountPath(cdnUrl);
    if (!mountPath) {
      continue;
    }
    if (url === origin) {
      return mountPath;
    }
    if (url.startsWith(`${origin}/`)) {
      return `${mountPath}${url.slice(origin.length)}`;
    }
  }

  return url;
};

/** Whether a request URL, as `toCdnRequestUrl` returns it, goes to a configured CDN origin. */
export const isCdnRequestUrl = (url: string): boolean =>
  url.startsWith(`${LOCAL_CDN_PROXY_PATH}/`) ||
  getProxiedCdnOrigins().some((cdnUrl) => {
    try {
      return url.startsWith(`${new URL(cdnUrl).origin}/`);
    } catch {
      return false;
    }
  });
